import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { sendChatMessage } from "../lib/api";

const SESSION_KEY = "hmf_chat_session_id";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm here to help with questions about Hosea Ministries. How can I help today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || "");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const onSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const r = await sendChatMessage(sessionRef.current || null, next);
      if (r.session_id) {
        sessionRef.current = r.session_id;
        localStorage.setItem(SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.reply || "I'm sorry, I couldn't answer that." }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            err?.response?.status === 429
              ? "Quick break — please send your next message in a moment."
              : "I'm having trouble right now. Please try again or call us at (918) 316-5889.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-brand-teal text-white shadow-xl hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 flex items-center justify-center"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          role="dialog"
          aria-label="Chat with Hosea Ministries"
          className="ph-no-capture fixed bottom-24 right-5 z-50 w-[calc(100%-2.5rem)] sm:w-[380px] max-h-[70vh] flex flex-col bg-white rounded-3xl shadow-2xl border border-brand-line overflow-hidden animate-fade-up"
        >
          <div className="bg-brand-teal text-white px-5 py-4">
            <div className="font-serif text-lg leading-tight">Ask Hosea</div>
            <div className="text-[11px] tracking-wider uppercase opacity-80">We usually reply instantly</div>
          </div>
          <p className="px-5 py-2 text-[11px] leading-snug bg-brand-bone text-brand-charcoal border-b border-brand-line" data-testid="chat-privacy-note">
            Please don't share personal details here (SSN, medical, or legal info). Use the secure application instead.
          </p>
          <div ref={listRef} aria-live="polite" className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-cream">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-brand-teal text-white ml-auto rounded-br-md"
                    : "bg-white border border-brand-line text-brand-charcoal rounded-bl-md"
                }`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="bg-white border border-brand-line rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-brand-muted max-w-[85%]">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" style={{ animationDelay: "0.4s" }} />
                </span>
              </div>
            )}
          </div>
          <form onSubmit={onSend} className="border-t border-brand-line bg-white p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              aria-label="Your question"
              data-testid="chat-input"
              className="flex-1 bg-brand-cream border border-brand-line rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/25 focus:border-brand-teal"
              disabled={loading}
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-brand-teal text-white flex items-center justify-center hover:bg-brand-tealDark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
